import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
const navLinks = [{
  name: 'Home',
  path: '/'
}, {
  name: 'About',
  path: '/about'
}, {
  name: 'Services',
  path: '/services'
}, {
  name: 'Leadership',
  path: '/leadership'
}, {
  name: 'Global Network',
  path: '/global-network'
}, {
  name: 'Contact',
  path: '/contact'
}];
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-navy/95 backdrop-blur-md shadow-lg' : 'bg-navy'}`} data-id="element-30">
        <div className="container mx-auto px-4 lg:px-8" data-id="element-31">
          <div className={`flex items-center justify-between transition-all duration-500 ${isScrolled ? 'h-16 lg:h-20' : 'h-44 sm:h-52 md:h-60 lg:h-64'}`} data-id="element-32">
            {/* Mobile: invisible spacer for centering logo */}
            <div className="lg:hidden w-10" data-id="element-33" />

            {/* Desktop Navigation - Left Group */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8 flex-1 justify-end pr-12" data-id="element-34">
              {navLinks.slice(0, 3).map(link => <Link key={link.path} to={link.path} className={`text-sm font-medium font-body transition-colors relative group whitespace-nowrap ${location.pathname === link.path ? 'text-gold' : 'text-white/90 hover:text-gold'}`} data-id="element-35">
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-gold transform origin-left transition-transform duration-300 ${location.pathname === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-36" />
                </Link>)}
            </nav>

            {/* Logo - Centered */}
            <Link to="/" className={`flex items-center flex-shrink-0 transition-all duration-500 ${isScrolled ? 'flex-row space-x-3 my-2' : 'flex-col mt-8 mb-3 lg:mt-10 lg:mb-4'}`} data-id="element-37">
              <img src="https://cdn.magicpatterns.com/uploads/hWmdhijhRkkJmHjpaF6M76/smg-logo.png" alt="Strategic Metals Group" className={`object-contain transition-all duration-500 ${isScrolled ? 'h-[40px] w-[40px] lg:h-[48px] lg:w-[48px]' : 'h-[80px] w-[80px] sm:h-[110px] sm:w-[110px] md:h-[140px] md:w-[140px] lg:h-[180px] lg:w-[180px]'}`} data-id="element-38" />
              {/* Default: text below, centered */}
              <span className={`text-white font-heading font-semibold leading-normal transition-all duration-500 ${isScrolled ? 'hidden' : 'text-center opacity-100 mt-1 pb-1'}`} data-id="element-39">
                <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl whitespace-nowrap" data-id="element-40">
                  Strategic Metals Group
                </span>
              </span>
              {/* Sticky: text to the right */}
              <span className={`text-white font-heading font-semibold leading-tight transition-all duration-500 ${isScrolled ? 'block opacity-100' : 'hidden opacity-0'}`} data-id="element-41">
                <span className="text-base lg:text-lg whitespace-nowrap" data-id="element-42">
                  Strategic Metals Group
                </span>
              </span>
            </Link>

            {/* Desktop Navigation - Right Group */}
            <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8 flex-1 pl-12" data-id="element-43">
              {navLinks.slice(3).map(link => <Link key={link.path} to={link.path} className={`text-sm font-medium font-body transition-colors relative group whitespace-nowrap ${location.pathname === link.path ? 'text-gold' : 'text-white/90 hover:text-gold'}`} data-id="element-44">
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-gold transform origin-left transition-transform duration-300 ${location.pathname === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-45" />
                </Link>)}
            </nav>

            {/* Mobile Menu Trigger */}
            <div className="lg:hidden" data-id="element-46">
              <Button variant="ghost" size="icon" className="text-white hover:text-gold" onClick={() => setIsOpen(true)} data-id="element-47">
                <Menu className="h-6 w-6" data-id="element-48" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sheet - rendered OUTSIDE header to avoid stacking context issues */}
      <Sheet open={isOpen} onOpenChange={setIsOpen} data-id="element-49">
        <SheetContent side="right" className="!bg-[#0a1628] border-gold/20 w-80 !z-[200]" style={{
        backgroundColor: '#0a1628',
        zIndex: 200
      }} data-id="element-50">
          <SheetHeader className="flex flex-row items-center justify-between pr-2" data-id="element-51">
            <SheetTitle className="text-white font-heading text-xl text-left" data-id="element-52">
              Menu
            </SheetTitle>
            <Button variant="ghost" size="icon" className="text-white hover:text-gold" onClick={() => setIsOpen(false)} data-id="element-53">
              <X className="h-6 w-6" data-id="element-54" />
            </Button>
          </SheetHeader>
          <nav className="flex flex-col space-y-1 mt-8" data-id="element-55">
            {navLinks.map(link => <Link key={link.path} to={link.path} onClick={() => setIsOpen(false)} className={`text-lg font-medium font-body transition-all duration-200 py-3 px-4 rounded-lg ${location.pathname === link.path ? 'text-gold bg-navy-light border-l-2 border-gold' : 'text-white/90 hover:text-gold hover:bg-navy-light'}`} data-id="element-56">
                {link.name}
              </Link>)}
          </nav>
        </SheetContent>
      </Sheet>
    </>;
}